import { createContext, useContext, useMemo, type ReactNode } from "react";
import {
  useConvexStaff,
  useConvexStaffRoles,
  useConvexProjects,
  useConvexMyTasks,
  useConvexAllThreadsForUser,
  useConvexMeetings,
  useConvexTrainingCourses,
  useConvexMyTrainingProgress,
  useConvexMyReminders,
  useConvexSiteFeatureToggles,
  useConvexSiteSettings,
  useConvexShiftRequests,
  useConvexRosterWeek,
  useConvexRosterShifts,
  useConvexClockEvents,
  type ConvexStaffMember,
  type ConvexStaffRole,
  type ConvexProject,
  type ConvexTask,
  type EnrichedThread,
  type ConvexMeeting,
  type ConvexTrainingCourse,
  type ConvexTrainingCompletion,
  type ConvexReminder,
  type ConvexSiteFeatureToggles,
  type ConvexSiteSettings,
  type ConvexShiftRequest,
  type ConvexRosterWeek,
  type ConvexRosterShift,
  type ConvexClockEvent,
} from "@/hooks/use-convex-data";
import { useSite } from "@/lib/site-context";
import { useStaff } from "@/lib/staff-context";
import { format, addDays, startOfWeek } from "date-fns";

interface ConvexCacheContextValue {
  staff: ConvexStaffMember[] | undefined;
  staffRoles: ConvexStaffRole[] | undefined;
  projects: ConvexProject[] | undefined;
  myTasks: ConvexTask[] | undefined;
  allThreads: EnrichedThread[] | undefined;
  meetings: ConvexMeeting[] | undefined;
  trainingCourses: ConvexTrainingCourse[] | undefined;
  trainingProgress: ConvexTrainingCompletion[] | undefined;
  myReminders: ConvexReminder[] | undefined;
  featureToggles: ConvexSiteFeatureToggles | null | undefined;
  siteSettings: ConvexSiteSettings | null | undefined;
  shiftRequests: ConvexShiftRequest[] | undefined;
  currentRosterWeek: ConvexRosterWeek | null | undefined;
  nextRosterWeek: ConvexRosterWeek | null | undefined;
  currentWeekShifts: ConvexRosterShift[] | undefined;
  nextWeekShifts: ConvexRosterShift[] | undefined;
  recentClockEvents: ConvexClockEvent[] | undefined;
  siteId: string | null;
}

const ConvexCacheContext = createContext<ConvexCacheContextValue | null>(null);

export function ConvexCacheProvider({ children }: { children: ReactNode }) {
  const { currentSite } = useSite();
  const siteId = currentSite?._id || null;
  const { selectedStaff } = useStaff();
  const staffMemberId = selectedStaff?.id || null;

  const todayKey = format(new Date(), "yyyy-MM-dd");
  const { currentWeekStart, nextWeekStart } = useMemo(() => {
    const today = new Date();
    const weekStart = startOfWeek(today, { weekStartsOn: 1 });
    return {
      currentWeekStart: format(weekStart, "yyyy-MM-dd"),
      nextWeekStart: format(addDays(weekStart, 7), "yyyy-MM-dd"),
    };
  }, [todayKey]);

  const staff = useConvexStaff(siteId);
  const staffRoles = useConvexStaffRoles(siteId);
  const projects = useConvexProjects(siteId);
  const myTasks = useConvexMyTasks(siteId);
  const allThreads = useConvexAllThreadsForUser(siteId);
  const meetings = useConvexMeetings(siteId);
  const trainingCourses = useConvexTrainingCourses(siteId);
  const trainingProgress = useConvexMyTrainingProgress(siteId);
  const myReminders = useConvexMyReminders(siteId);
  const featureToggles = useConvexSiteFeatureToggles(siteId);
  const siteSettings = useConvexSiteSettings(siteId);
  const shiftRequests = useConvexShiftRequests(siteId);

  const currentRosterWeek = useConvexRosterWeek(siteId, currentWeekStart);
  const nextRosterWeek = useConvexRosterWeek(siteId, nextWeekStart);
  const currentWeekShifts = useConvexRosterShifts(currentRosterWeek?._id || null);
  const nextWeekShifts = useConvexRosterShifts(nextRosterWeek?._id || null);
  const recentClockEvents = useConvexClockEvents(staffMemberId, 20);

  return (
    <ConvexCacheContext.Provider
      value={{
        staff,
        staffRoles,
        projects,
        myTasks,
        allThreads,
        meetings,
        trainingCourses,
        trainingProgress,
        myReminders,
        featureToggles,
        siteSettings,
        shiftRequests,
        currentRosterWeek,
        nextRosterWeek,
        currentWeekShifts,
        nextWeekShifts,
        recentClockEvents,
        siteId,
      }}
    >
      {children}
    </ConvexCacheContext.Provider>
  );
}

function useConvexCache(): ConvexCacheContextValue {
  const ctx = useContext(ConvexCacheContext);
  if (!ctx) {
    throw new Error("useConvexCache must be used within ConvexCacheProvider");
  }
  return ctx;
}

export function useCachedStaff(): ConvexStaffMember[] | undefined {
  return useConvexCache().staff;
}

export function useCachedStaffRoles(): ConvexStaffRole[] | undefined {
  return useConvexCache().staffRoles;
}

export function useCachedProjects(): ConvexProject[] | undefined {
  return useConvexCache().projects;
}

export function useCachedMyTasks(): ConvexTask[] | undefined {
  return useConvexCache().myTasks;
}

export function useCachedAllThreads(): EnrichedThread[] | undefined {
  return useConvexCache().allThreads;
}

export function useCachedMeetings(): ConvexMeeting[] | undefined {
  return useConvexCache().meetings;
}

export function useCachedTrainingCourses(): ConvexTrainingCourse[] | undefined {
  return useConvexCache().trainingCourses;
}

export function useCachedTrainingProgress(): ConvexTrainingCompletion[] | undefined {
  return useConvexCache().trainingProgress;
}

export function useCachedMyReminders(): ConvexReminder[] | undefined {
  return useConvexCache().myReminders;
}

export function useCachedFeatureToggles(): ConvexSiteFeatureToggles | null | undefined {
  return useConvexCache().featureToggles;
}

export function useCachedSiteSettings(): ConvexSiteSettings | null | undefined {
  return useConvexCache().siteSettings;
}

export function useCachedShiftRequests(): ConvexShiftRequest[] | undefined {
  return useConvexCache().shiftRequests;
}

export function useCachedCurrentRosterWeek(): ConvexRosterWeek | null | undefined {
  return useConvexCache().currentRosterWeek;
}

export function useCachedNextRosterWeek(): ConvexRosterWeek | null | undefined {
  return useConvexCache().nextRosterWeek;
}

export function useCachedCurrentWeekShifts(): ConvexRosterShift[] | undefined {
  return useConvexCache().currentWeekShifts;
}

export function useCachedNextWeekShifts(): ConvexRosterShift[] | undefined {
  return useConvexCache().nextWeekShifts;
}

export function useCachedRecentClockEvents(): ConvexClockEvent[] | undefined {
  return useConvexCache().recentClockEvents;
}

export function useCachedSiteId(): string | null {
  return useConvexCache().siteId;
}
